import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  SentimentAnalysisStatsService,
  type TaxonomyUploadPayload,
} from '../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';
import {
  SENTIMENT_ANALYSIS_CONFIG,
  type GettingStartedStepKey,
} from '../sentiment-analysis.config';
import { FeedsService } from '../../../services/feeds/feeds.service';
import { AppStateService } from '../../../services/helpers/app-state.service';
import { SentimentAnalysisTaxonomyPreviewComponent } from '../sentiment-analysis-taxonomy-preview/sentiment-analysis-taxonomy-preview.component';
import {
  buildProjectConfigFromDraft,
  cohortFieldOptionsFromBrandDetails,
  createEmptySetupDraft,
  SUMMARY_PRECOMPUTE_SENTIMENT_CHOICES,
  resolveRatingAlignmentFromMainRaterType,
  summarySentimentLabel,
  toggleSummaryPrecomputeSentiment,
  type RatingAlignmentScale,
  resolveBrandDetailsForCohortSetup,
  taxonomyThemeNamesFromDraft,
  type BrandCohortFieldOption,
  type SentimentSetupDraft,
} from '../sentiment-analysis-setup.model';

/** Which config path cohort checkboxes apply to. */
type CohortSelectionTarget = 'heatmap' | 'summary';

@Component({
  selector: 'app-sentiment-analysis-getting-started',
  standalone: true,
  imports: [CommonModule, FormsModule, SentimentAnalysisTaxonomyPreviewComponent],
  templateUrl: './sentiment-analysis-getting-started.component.html',
  styleUrls: ['./sentiment-analysis-getting-started.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SentimentAnalysisGettingStartedComponent implements OnInit, OnChanges {
  readonly config = SENTIMENT_ANALYSIS_CONFIG;
  readonly steps = this.config.copy.gettingStartedSteps;
  readonly industryCopy = this.config.copy.gettingStartedIndustry;
  readonly taxonomyCopy = this.config.copy.gettingStartedTaxonomy;
  readonly heatmapCohortsCopy = this.config.copy.gettingStartedHeatmapCohorts;
  readonly summaryCohortsCopy = this.config.copy.gettingStartedSummaryCohorts;
  readonly analysisInputCopy = this.config.copy.gettingStartedAnalysisInput;
  readonly promptCopy = this.config.copy.gettingStartedPrompt;
  readonly summaryCopy = this.config.copy.gettingStartedSummary;
  readonly summarySentimentChoices = SUMMARY_PRECOMPUTE_SENTIMENT_CHOICES;
  readonly completeCopy = this.config.copy.gettingStartedComplete;
  readonly buttons = this.config.copy.gettingStartedButtons;

  readonly taxonomyThemeModeOptions = [
    { value: 'names_only', label: 'Names only' },
    { value: 'names_and_descriptions', label: 'Names and descriptions' },
    { value: 'names_descriptions_and_keywords', label: 'Names, descriptions, and keywords' },
  ] as const;

  @Input() brandIdForApi = '';
  @Input() canUpload = true;
  @Input() brandDetails: Record<string, unknown> | null = null;

  @Output() readonly configReady = new EventEmitter<void>();
  @Output() readonly message = new EventEmitter<{ text: string; kind: 'info' | 'error' | 'warn' }>();

  activeStep: GettingStartedStepKey = 'industry';
  draft: SentimentSetupDraft = createEmptySetupDraft();
  configSaved = false;
  saving = false;

  cohortFieldOptions: BrandCohortFieldOption[] = [];
  selectedHeatmapCohortFieldIds = new Set<string>();
  selectedSummaryCohortFieldIds = new Set<string>();
  cohortFieldsLoading = false;
  private heatmapCohortDefaultsApplied = false;
  private summaryCohortDefaultsApplied = false;
  private heatmapCohortUserTouched = false;
  private summaryCohortUserTouched = false;
  private cohortDefaultsBrandId = '';
  /** `null` until brand has a published NPS or CSAT primary rating (`main_rater_type`). */
  ratingAlignmentResolved: RatingAlignmentScale | null = null;

  taxonomyCsvFileName = '';
  taxonomyPreview: TaxonomyUploadPayload | null = null;
  taxonomyPreviewError: string | null = null;
  taxonomyPreviewLoading = false;
  summaryThemeToAdd = '';
  private summaryThemeAllowlistTouched = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly statsService = inject(SentimentAnalysisStatsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly appStateService = inject(AppStateService);
  private readonly feedsService = inject(FeedsService);

  constructor() {
    effect(() => {
      const bd = this.appStateService.brandDetails();
      untracked(() => {
        this.refreshRatingFromBrand(bd as Record<string, unknown> | null);
      });
    });
  }

  ngOnInit(): void {
    this.refreshBrandDerivedState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['brandIdForApi']) {
      const prev = String(changes['brandIdForApi'].previousValue ?? '').trim();
      const cur = String(changes['brandIdForApi'].currentValue ?? '').trim();
      if (prev && cur && prev !== cur) {
        this.resetCohortSelectionState();
      }
    }
    if (changes['brandDetails']) {
      const bd = this.brandDetails;
      this.refreshRatingFromBrand(bd);
      if (this.activeStep === 'heatmap_cohorts' || this.activeStep === 'summary') {
        this.loadCohortFieldOptionsOnly();
      }
    }
  }

  private resetCohortSelectionState(): void {
    this.heatmapCohortDefaultsApplied = false;
    this.summaryCohortDefaultsApplied = false;
    this.heatmapCohortUserTouched = false;
    this.summaryCohortUserTouched = false;
    this.selectedHeatmapCohortFieldIds = new Set<string>();
    this.selectedSummaryCohortFieldIds = new Set<string>();
    this.draft.heatmapCohortFieldIds = [];
    this.draft.summaryCohortFieldIds = [];
  }

  get activeStepIndex(): number {
    return this.steps.findIndex((s) => s.key === this.activeStep);
  }

  get isFirstStep(): boolean {
    return this.activeStepIndex <= 0;
  }

  trackCohortField(_index: number, opt: BrandCohortFieldOption): string {
    return opt.fieldId;
  }

  get taxonomyThemeNames(): string[] {
    return taxonomyThemeNamesFromDraft(this.draft);
  }

  get themesAvailableToAdd(): string[] {
    const inAllowlist = new Set(this.draft.summaryThemeAllowlist);
    return this.taxonomyThemeNames.filter((n) => !inAllowlist.has(n));
  }

  get isRatingAlignmentConfigured(): boolean {
    return this.ratingAlignmentResolved != null;
  }

  get canSaveFinalConfig(): boolean {
    const hasTaxonomy = !!this.draft.taxonomy?.themes?.length;
    const allowlistOk =
      !this.draft.summaryPrecomputeEnabled ||
      this.draft.summaryThemeAllowlist.length > 0 ||
      !hasTaxonomy;
    const sentimentsOk =
      !this.draft.summaryPrecomputeEnabled || this.draft.summaryPrecomputeSentiments.length > 0;
    return (
      this.canUpload &&
      !!this.brandIdForApi &&
      hasTaxonomy &&
      String(this.draft.industryName ?? '').trim().length > 0 &&
      allowlistOk &&
      sentimentsOk &&
      this.isRatingAlignmentConfigured
    );
  }

  refreshBrandDerivedState(appStateBrand?: Record<string, unknown> | null): void {
    const bd = resolveBrandDetailsForCohortSetup(
      this.brandDetails,
      appStateBrand ?? (this.appStateService.brandDetails() as Record<string, unknown> | null),
    );
    this.refreshRatingFromBrand(bd);
    this.cdr.markForCheck();
  }

  private resolveBrandForCohorts(): Record<string, unknown> | null {
    return resolveBrandDetailsForCohortSetup(
      this.brandDetails,
      this.appStateService.brandDetails() as Record<string, unknown> | null,
    );
  }

  /** Load tag list only — does not change checkbox selection after user interaction. */
  private loadCohortFieldOptionsOnly(): void {
    const options = cohortFieldOptionsFromBrandDetails(this.resolveBrandForCohorts());
    this.setCohortFieldOptions(options);
    this.cdr.markForCheck();
  }

  private refreshRatingFromBrand(bd: Record<string, unknown> | null | undefined): void {
    const resolved = resolveBrandDetailsForCohortSetup(this.brandDetails, bd ?? null) ?? bd;
    const mainRater = resolved?.['main_rater_type'];
    this.ratingAlignmentResolved = resolveRatingAlignmentFromMainRaterType(mainRater);
  }

  private onEnterAnalysisInputStep(): void {
    const bd = resolveBrandDetailsForCohortSetup(
      this.brandDetails,
      this.appStateService.brandDetails() as Record<string, unknown> | null,
    );
    this.refreshRatingFromBrand(bd);
    this.cdr.markForCheck();
  }

  private setCohortFieldOptions(options: BrandCohortFieldOption[]): void {
    const brandId = String(this.brandIdForApi ?? '').trim();
    if (brandId && this.cohortDefaultsBrandId && brandId !== this.cohortDefaultsBrandId) {
      this.resetCohortSelectionState();
    }
    if (brandId) {
      this.cohortDefaultsBrandId = brandId;
    }

    this.cohortFieldOptions = options;
    const labels = { ...this.draft.cohortLabels };
    for (const o of options) {
      labels[o.fieldId] = o.label;
    }
    this.draft.cohortLabels = labels;

    if (!options.length) {
      return;
    }

    const validIds = new Set(options.map((o) => o.fieldId));
    this.selectedHeatmapCohortFieldIds = this.pruneCohortSet(
      this.selectedHeatmapCohortFieldIds,
      this.heatmapCohortUserTouched,
      this.draft.heatmapCohortFieldIds,
      validIds,
    );
    this.selectedSummaryCohortFieldIds = this.pruneCohortSet(
      this.selectedSummaryCohortFieldIds,
      this.summaryCohortUserTouched,
      this.draft.summaryCohortFieldIds,
      validIds,
    );
    this.syncDraftCohortIdsFromSets();
  }

  private pruneCohortSet(
    current: Set<string>,
    userTouched: boolean,
    draftIds: string[],
    validIds: Set<string>,
  ): Set<string> {
    if (userTouched || current.size > 0) {
      return new Set([...current].filter((id) => validIds.has(id)));
    }
    if (draftIds.length > 0) {
      return new Set(draftIds.filter((id) => validIds.has(id)));
    }
    return current;
  }

  private cohortSetFor(target: CohortSelectionTarget): Set<string> {
    return target === 'heatmap' ? this.selectedHeatmapCohortFieldIds : this.selectedSummaryCohortFieldIds;
  }

  isCohortFieldSelected(fieldId: string, target: CohortSelectionTarget): boolean {
    return this.cohortSetFor(target).has(fieldId);
  }

  cohortSelectedCount(target: CohortSelectionTarget): number {
    return this.cohortSetFor(target).size;
  }

  private applyInitialCohortDefaultsFor(target: CohortSelectionTarget): void {
    if (!this.cohortFieldOptions.length) {
      return;
    }
    if (target === 'heatmap') {
      if (this.heatmapCohortDefaultsApplied || this.heatmapCohortUserTouched) {
        return;
      }
      const ids = this.defaultCohortFieldIdsFromBrand();
      this.selectedHeatmapCohortFieldIds = new Set(ids);
      this.heatmapCohortDefaultsApplied = true;
    } else {
      if (this.summaryCohortDefaultsApplied || this.summaryCohortUserTouched) {
        return;
      }
      const ids =
        this.selectedHeatmapCohortFieldIds.size > 0
          ? [...this.selectedHeatmapCohortFieldIds]
          : this.defaultCohortFieldIdsFromBrand();
      this.selectedSummaryCohortFieldIds = new Set(ids);
      this.summaryCohortDefaultsApplied = true;
    }
    this.syncDraftCohortIdsFromSets();
  }

  private defaultCohortFieldIdsFromBrand(): string[] {
    const preselected = this.cohortFieldOptions.filter((o) => o.enabledOnBrand);
    return preselected.length > 0
      ? preselected.map((o) => o.fieldId)
      : this.cohortFieldOptions.map((o) => o.fieldId);
  }

  private syncDraftCohortIdsFromSets(): void {
    this.draft.heatmapCohortFieldIds = this.cohortFieldOptions
      .filter((o) => this.selectedHeatmapCohortFieldIds.has(o.fieldId))
      .map((o) => o.fieldId);
    this.draft.summaryCohortFieldIds = this.cohortFieldOptions
      .filter((o) => this.selectedSummaryCohortFieldIds.has(o.fieldId))
      .map((o) => o.fieldId);
  }

  /** Load `field_definitions` from API when brand snapshot has no tag list yet. */
  loadCohortFieldsIfNeeded(): void {
    this.loadCohortFieldOptionsOnly();
    if (this.cohortFieldOptions.length) {
      return;
    }
    if (!this.brandIdForApi || this.cohortFieldsLoading) {
      return;
    }

    this.cohortFieldsLoading = true;
    this.cdr.markForCheck();

    this.feedsService
      .getFieldDefinitions({ brand_id: this.brandIdForApi })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.cohortFieldsLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: unknown) => {
          const r = res as Record<string, unknown>;
          const data = r['data'] as Record<string, unknown> | undefined;
          const defs =
            (Array.isArray(data?.['field_definitions']) ? data['field_definitions'] : null) ??
            (Array.isArray(r['field_definitions']) ? r['field_definitions'] : []);
          const bd = resolveBrandDetailsForCohortSetup(this.brandDetails, null);
          const merged: Record<string, unknown> = {
            ...(bd ?? {}),
            field_definitions: defs,
          };
          this.setCohortFieldOptions(cohortFieldOptionsFromBrandDetails(merged));
          this.cdr.detectChanges();
        },
        error: () => {
          this.cdr.markForCheck();
        },
      });
  }

  private onEnterHeatmapCohortsStep(): void {
    this.loadCohortFieldsIfNeeded();
    this.applyInitialCohortDefaultsFor('heatmap');
    this.cdr.markForCheck();
  }

  isStepComplete(stepKey: GettingStartedStepKey): boolean {
    switch (stepKey) {
      case 'industry':
        return String(this.draft.industryName ?? '').trim().length > 0;
      case 'taxonomy':
        return !!this.draft.taxonomy?.themes?.length;
      case 'heatmap_cohorts':
        return this.draft.heatmapCohortFieldIds.length > 0 || this.cohortFieldOptions.length === 0;
      case 'analysis_input':
        return this.isRatingAlignmentConfigured;
      case 'prompt':
        return true;
      case 'summary': {
        const cohortsOk =
          this.draft.summaryCohortFieldIds.length > 0 || this.cohortFieldOptions.length === 0;
        const allowlistOk =
          !this.draft.summaryPrecomputeEnabled ||
          this.draft.summaryThemeAllowlist.length > 0 ||
          this.taxonomyThemeNames.length === 0;
        const sentimentsOk =
          !this.draft.summaryPrecomputeEnabled || this.draft.summaryPrecomputeSentiments.length > 0;
        return cohortsOk && allowlistOk && sentimentsOk;
      }
      case 'complete':
        return this.configSaved;
      default:
        return false;
    }
  }

  canGoNext(): boolean {
    return this.isStepComplete(this.activeStep) && this.activeStep !== 'complete';
  }

  goNext(): void {
    if (!this.canGoNext()) {
      return;
    }
    const idx = this.activeStepIndex;
    if (idx < 0 || idx >= this.steps.length - 1) {
      return;
    }
    const nextKey = this.steps[idx + 1].key;
    this.activeStep = nextKey;
    if (nextKey === 'heatmap_cohorts') {
      this.onEnterHeatmapCohortsStep();
    } else if (nextKey === 'analysis_input') {
      this.onEnterAnalysisInputStep();
    } else if (nextKey === 'summary') {
      this.onEnterSummaryStep();
    }
    this.cdr.markForCheck();
  }

  goBack(): void {
    const idx = this.activeStepIndex;
    if (idx <= 0) {
      return;
    }
    this.activeStep = this.steps[idx - 1].key;
    this.cdr.markForCheck();
  }

  goToStep(key: GettingStartedStepKey): void {
    if (key === 'complete' && !this.configSaved) {
      return;
    }
    const targetIdx = this.steps.findIndex((s) => s.key === key);
    for (let i = 0; i < targetIdx; i++) {
      const stepKey = this.steps[i].key;
      if (stepKey !== 'complete' && !this.isStepComplete(stepKey)) {
        return;
      }
    }
    this.activeStep = key;
    if (key === 'heatmap_cohorts') {
      this.onEnterHeatmapCohortsStep();
    } else if (key === 'analysis_input') {
      this.onEnterAnalysisInputStep();
    } else if (key === 'summary') {
      this.onEnterSummaryStep();
    }
    this.cdr.markForCheck();
  }

  private onEnterSummaryStep(): void {
    this.loadCohortFieldsIfNeeded();
    this.applyInitialCohortDefaultsFor('summary');
    this.syncSummaryThemeAllowlistFromTaxonomy(false);
    this.cdr.markForCheck();
  }

  /** (Re)fill allowlist from taxonomy when empty or after new taxonomy upload. */
  private syncSummaryThemeAllowlistFromTaxonomy(resetToAll: boolean): void {
    const names = this.taxonomyThemeNames;
    if (!names.length) {
      this.draft.summaryThemeAllowlist = [];
      return;
    }
    if (resetToAll || !this.summaryThemeAllowlistTouched || this.draft.summaryThemeAllowlist.length === 0) {
      this.draft.summaryThemeAllowlist = [...names];
      if (resetToAll) {
        this.summaryThemeAllowlistTouched = false;
      }
      return;
    }
    const valid = new Set(names);
    this.draft.summaryThemeAllowlist = this.draft.summaryThemeAllowlist.filter((n) => valid.has(n));
  }

  removeThemeFromAllowlist(themeName: string): void {
    const name = String(themeName ?? '').trim();
    if (!name) {
      return;
    }
    this.summaryThemeAllowlistTouched = true;
    this.draft.summaryThemeAllowlist = this.draft.summaryThemeAllowlist.filter((t) => t !== name);
    this.cdr.markForCheck();
  }

  addSelectedThemeToAllowlist(): void {
    const name = String(this.summaryThemeToAdd ?? '').trim();
    if (!name || this.draft.summaryThemeAllowlist.includes(name)) {
      return;
    }
    if (!this.taxonomyThemeNames.includes(name)) {
      return;
    }
    this.summaryThemeAllowlistTouched = true;
    this.draft.summaryThemeAllowlist = [...this.draft.summaryThemeAllowlist, name];
    this.summaryThemeToAdd = '';
    this.cdr.markForCheck();
  }

  addAllTaxonomyThemesToAllowlist(): void {
    this.summaryThemeAllowlistTouched = true;
    this.draft.summaryThemeAllowlist = [...this.taxonomyThemeNames];
    this.summaryThemeToAdd = '';
    this.cdr.markForCheck();
  }

  onTaxonomyCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file || !this.brandIdForApi) {
      return;
    }

    this.taxonomyCsvFileName = file.name;
    this.taxonomyPreview = null;
    this.draft.taxonomy = null;
    this.taxonomyPreviewError = null;
    this.taxonomyPreviewLoading = true;
    this.cdr.markForCheck();

    this.statsService.previewTaxonomyFromCsvFile(file).subscribe({
      next: (taxonomy) => {
        this.taxonomyPreviewLoading = false;
        this.taxonomyPreview = taxonomy;
        if (!taxonomy.themes.length) {
          this.taxonomyPreviewError =
            'No valid rows found. Add at least one theme row in the CSV.';
        } else {
          this.draft.taxonomy = taxonomy;
          this.syncSummaryThemeAllowlistFromTaxonomy(true);
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.taxonomyPreviewLoading = false;
        this.taxonomyPreview = null;
        this.draft.taxonomy = null;
        this.taxonomyPreviewError = String(err?.message || err || 'Failed to parse CSV');
        this.cdr.markForCheck();
      },
    });
  }

  onCohortCheckboxClick(
    fieldId: string,
    label: string,
    target: CohortSelectionTarget,
    event: MouseEvent,
  ): void {
    event.preventDefault();
    event.stopPropagation();

    if (target === 'heatmap') {
      this.heatmapCohortUserTouched = true;
      this.heatmapCohortDefaultsApplied = true;
    } else {
      this.summaryCohortUserTouched = true;
      this.summaryCohortDefaultsApplied = true;
    }

    const current = this.cohortSetFor(target);
    const next = new Set(current);
    if (next.has(fieldId)) {
      next.delete(fieldId);
    } else {
      next.add(fieldId);
    }
    if (target === 'heatmap') {
      this.selectedHeatmapCohortFieldIds = next;
    } else {
      this.selectedSummaryCohortFieldIds = next;
    }
    this.draft.cohortLabels = { ...this.draft.cohortLabels, [fieldId]: label };
    this.syncDraftCohortIdsFromSets();
    this.cdr.detectChanges();
  }

  copySummaryCohortsFromHeatmap(): void {
    this.selectedSummaryCohortFieldIds = new Set(this.selectedHeatmapCohortFieldIds);
    this.summaryCohortUserTouched = true;
    this.summaryCohortDefaultsApplied = true;
    this.syncDraftCohortIdsFromSets();
    this.cdr.detectChanges();
  }

  isSummarySentimentSelected(sentimentId: string): boolean {
    return this.draft.summaryPrecomputeSentiments.includes(String(sentimentId ?? '').trim().toLowerCase());
  }

  summarySentimentChoiceLabel(sentimentId: string): string {
    return summarySentimentLabel(sentimentId);
  }

  onSummarySentimentCheckboxClick(sentimentId: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.draft.summaryPrecomputeSentiments = toggleSummaryPrecomputeSentiment(
      this.draft.summaryPrecomputeSentiments,
      sentimentId,
    );
    this.cdr.detectChanges();
  }

  saveFullConfiguration(): void {
    if (!this.canSaveFinalConfig) {
      return;
    }

    let configPatch: Record<string, unknown>;
    try {
      if (!this.ratingAlignmentResolved) {
        this.message.emit({
          text: this.config.copy.gettingStartedAnalysisInput.notConfiguredLead,
          kind: 'error',
        });
        return;
      }
      configPatch = buildProjectConfigFromDraft(this.draft, this.ratingAlignmentResolved);
    } catch (err) {
      this.message.emit({
        text: String((err as Error)?.message || err || 'Invalid setup draft'),
        kind: 'error',
      });
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    this.statsService.saveProjectConfig(this.brandIdForApi, configPatch).subscribe({
      next: (res) => {
        this.saving = false;
        this.configSaved = true;
        const version = res?.configuration_version ?? '?';
        const tc = res?.taxonomy?.theme_count ?? 0;
        const stc = res?.taxonomy?.subtheme_count ?? 0;
        this.statsService.clearSentimentConfigCache(this.brandIdForApi);
        this.message.emit({
          text: `Configuration saved (version ${version}, ${tc} themes, ${stc} sub-themes).`,
          kind: 'info',
        });
        this.activeStep = 'complete';
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.saving = false;
        this.message.emit({
          text: String(err?.message || err || 'Configuration save failed'),
          kind: 'error',
        });
        this.cdr.markForCheck();
      },
    });
  }

  finishAndLoadDashboard(): void {
    this.configReady.emit();
  }
}
